import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { ChannelPickFilters, ChannelPickSort, ShippingChannel } from '@mypoz/shared';
import {
  CHANNEL_PICK_SORTS,
  channelTransportTypes,
  clampPackagePage,
  EMPTY_CHANNEL_PICK_FILTERS,
  filterChannelPicks,
  ORDER_CHANNELS_PER_PAGE,
  packagePageCount,
  pageOf,
} from '@mypoz/shared';
import { useCodeLabel, useTranslation } from '@mypoz/i18n';
import { Card, Icon, cn } from '@mypoz/ui';

import { WEIGHT_BASIS } from '@/features/orders/dictionary.js';
import {
  PickerChips,
  PickerEmpty,
  PickerSearch,
  PickerSort,
} from '@/features/orders/components/PickerControls.js';
import { Pager } from '@/components/Pager.js';
import { TRANSPORT_TYPE } from '@/features/packages/index.js';
import { useFormat } from '@/lib/format.js';

export interface ChannelPickerProps {
  channels: readonly ShippingChannel[];
  selectedId: string | undefined;
  /**
   * The selected channel's name when `channels` does not include it — an existing order whose
   * channel the vendor no longer offers for this selection (69 of 284 orders on the test account
   * use a channel `channels` does not list). The choice is kept and named, not silently dropped.
   */
  selectedName?: string;
  onSelect: (channelId: string) => void;
}

/**
 * Step 2 — choosing how the order travels.
 *
 * Notably priceless, and that is the design rather than a gap: no channel endpoint returns a
 * figure, because a forwarding order is quoted only once the warehouse has weighed and packed it.
 * So the choice is made on transit time, transport type, which basis the channel charges weight
 * on, and whatever the vendor's own notice for it says — and the copy says plainly when the price
 * arrives, rather than leaving the reader hunting for a number that was never going to be there.
 */
export function ChannelPicker({
  channels,
  selectedId,
  selectedName,
  onSelect,
}: ChannelPickerProps) {
  const { t } = useTranslation();
  const codeLabel = useCodeLabel();

  // Same reasoning as the package step: local state, and any filter change goes back to page 1.
  const [filters, setFilters] = useState<ChannelPickFilters>(EMPTY_CHANNEL_PICK_FILTERS);
  const [requestedPage, setPage] = useState(1);
  const changeFilters = (next: ChannelPickFilters) => {
    setFilters(next);
    setPage(1);
  };
  // phone order layout: whether the phone toolbar shows the search box instead of the chips (see below).
  const [searchOpen, setSearchOpen] = useState(false);
  const searchShown = searchOpen || filters.search !== '';
  const searchInput = useRef<HTMLInputElement>(null);
  const searchButton = useRef<HTMLButtonElement>(null);
  // Focus follows the toggle: into the box when it opens, back to its button when it closes.
  const reopenFocus = useRef(false);
  useEffect(() => {
    if (searchOpen) {
      searchInput.current?.focus();
    } else if (reopenFocus.current) {
      reopenFocus.current = false;
      searchButton.current?.focus();
    }
  }, [searchOpen]);
  // Cancel and Escape: clear the search and give the row back to the chips.
  const closeSearch = () => {
    changeFilters({ ...filters, search: '' });
    reopenFocus.current = searchOpen;
    setSearchOpen(false);
  };

  const shown = filterChannelPicks(channels, filters);
  const page = clampPackagePage(requestedPage, shown.length, ORDER_CHANNELS_PER_PAGE);
  const onPage = pageOf(shown, page, ORDER_CHANNELS_PER_PAGE);
  const selectedLabel =
    channels.find((channel) => channel.id === selectedId)?.name ??
    (selectedId !== undefined ? (selectedName ?? selectedId) : undefined);

  const sortLabels: Record<ChannelPickSort, string> = {
    default: t('orders.picker.channels.sort.default'),
    fastest: t('orders.picker.channels.sort.fastest'),
    name: t('orders.picker.channels.sort.name'),
  };

  // MYP-131: the red border says which channel is chosen, so the "Selected channel" line shows
  // only when that card is not on screen — on another page, hidden by the search, or not in the
  // list at all (an existing order's own channel, see `selectedName`).
  const selectedOnScreen = onPage.some((channel) => channel.id === selectedId);

  return (
    <fieldset className="@container">
      <legend className="sr-only">{t('orders.newOrder.channel.legend')}</legend>

      {selectedLabel !== undefined && !selectedOnScreen ? (
        <p className="mb-3 flex items-center gap-2 text-sm text-ink">
          <Icon name="check_circle" size={18} className="shrink-0 text-brand" fill />
          <span className="min-w-0">
            {t('orders.picker.channels.selected')}{' '}
            <span className="font-semibold">{selectedLabel}</span>
          </span>
        </p>
      ) : null}

      {/*
        MYP-131: search, transport chips and sort in one row on a laptop.
        phone order layout: one row on a phone too — the chips, then a search button and the sort icon. The
        search button swaps the row for the search box (with Cancel); a search in progress keeps
        the box open, so the words being searched for never hide.
      */}
      <div className="mb-3 flex items-center gap-2 sm:flex-wrap sm:gap-3">
        <PickerSearch
          label={t('orders.picker.channels.searchLabel')}
          placeholder={t('orders.picker.channels.searchPlaceholder')}
          value={filters.search}
          onChange={(search) => changeFilters({ ...filters, search })}
          inputRef={searchInput}
          onEscape={closeSearch}
          className={cn('sm:block sm:w-80 sm:flex-none', searchShown ? 'flex-1' : 'hidden')}
        />
        {searchShown ? (
          <button
            type="button"
            onClick={closeSearch}
            className="min-h-11 shrink-0 rounded-md px-2 text-sm font-semibold text-brand hover:bg-brand-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand sm:hidden"
          >
            {t('orders.picker.closeSearch')}
          </button>
        ) : null}
        <PickerChips
          label={t('orders.picker.channels.transportLabel')}
          value={filters.transport}
          options={[
            { value: 'all', label: t('orders.picker.channels.transportAll') },
            ...channelTransportTypes(channels).map((code) => ({
              value: code,
              label: codeLabel(TRANSPORT_TYPE, code),
            })),
          ]}
          onChange={(transport) => changeFilters({ ...filters, transport })}
          className={cn(
            'min-w-0 flex-1 flex-nowrap overflow-x-auto sm:flex-none sm:flex-wrap sm:overflow-visible',
            searchShown && 'hidden sm:flex',
          )}
        />
        {searchShown ? null : (
          <button
            ref={searchButton}
            type="button"
            aria-label={t('orders.picker.channels.searchLabel')}
            onClick={() => setSearchOpen(true)}
            className="flex size-11 shrink-0 items-center justify-center rounded-full border border-line bg-surface text-ink transition-colors hover:bg-surface-alt focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand sm:hidden"
          >
            <Icon name="search" size={20} />
          </button>
        )}
        <div className={cn('sm:ml-auto', searchShown && 'hidden sm:block')}>
          <PickerSort
            value={filters.sort}
            defaultValue={EMPTY_CHANNEL_PICK_FILTERS.sort}
            options={CHANNEL_PICK_SORTS.map((value) => ({ value, label: sortLabels[value] }))}
            onChange={(sort) => changeFilters({ ...filters, sort })}
          />
        </div>
      </div>

      {shown.length === 0 ? (
        <Card className="p-0">
          <PickerEmpty
            message={t('orders.picker.channels.noMatch')}
            onReset={() => changeFilters(EMPTY_CHANNEL_PICK_FILTERS)}
          />
        </Card>
      ) : null}

      {/*
        MYP-131: the columns follow the space the list has, not the screen. Three from about
        900px (a 1366px laptop beside the sidebar), four on a wide monitor, two from 580px, one
        below: a card stays about 290px or wider, enough for the name, transit, charge basis and
        two lines of notice without shrinking the text.
      */}
      <div className="grid gap-3 @xl:grid-cols-2 @4xl:grid-cols-3 @6xl:grid-cols-4">
        {onPage.map((channel) => (
          <ChannelCard
            key={channel.id}
            channel={channel}
            selected={channel.id === selectedId}
            onSelect={onSelect}
          />
        ))}
      </div>

      <Pager
        label={t('orders.picker.channels.pagerLabel')}
        page={page}
        pageCount={packagePageCount(shown.length, ORDER_CHANNELS_PER_PAGE)}
        rangeStart={(page - 1) * ORDER_CHANNELS_PER_PAGE + 1}
        rangeEnd={(page - 1) * ORDER_CHANNELS_PER_PAGE + onPage.length}
        total={shown.length}
        onGo={setPage}
      />

      <p className="mt-4 border-l-2 border-brand pl-3 text-sm text-ink">
        {t('orders.newOrder.channel.noPrice')}
      </p>
    </fieldset>
  );
}

/**
 * One channel (MYP-131: tighter padding, and a long notice cut to two lines with "Show more").
 *
 * The whole card selects the channel through a label laid over it, as `ServiceCard` does, so the
 * "Show more" button can sit inside the card without being part of the label.
 */
function ChannelCard({
  channel,
  selected,
  onSelect,
}: {
  channel: ShippingChannel;
  selected: boolean;
  onSelect: (channelId: string) => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const codeLabel = useCodeLabel();
  const inputId = useId();
  const nameId = useId();
  const noticeId = useId();
  const noticeRef = useRef<HTMLSpanElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [truncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    const element = noticeRef.current;
    if (!element || expanded) return;
    const measure = () => setTruncated(element.scrollHeight > element.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [expanded, channel.notice]);

  return (
    <Card
      className={cn(
        'relative p-0 transition-colors',
        selected && 'ring-2 ring-brand ring-offset-2',
      )}
    >
      <label htmlFor={inputId} aria-hidden="true" className="absolute inset-0 cursor-pointer" />
      <div className="flex h-full flex-col gap-2 p-4">
        <span className="flex items-start gap-3">
          <input
            id={inputId}
            type="radio"
            name="shipping-channel"
            checked={selected}
            onChange={() => onSelect(channel.id)}
            aria-labelledby={nameId}
            aria-describedby={channel.notice ? noticeId : undefined}
            className="relative mt-0.5 size-5 shrink-0 accent-brand"
          />
          <span id={nameId} className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-ink">{channel.name}</span>
            {channel.nameNative ? (
              <span lang="zh" className="block text-sm text-muted">
                {channel.nameNative}
              </span>
            ) : null}
          </span>
          <span className="shrink-0 rounded bg-brand-soft px-2 py-0.5 text-xs font-semibold text-brand">
            {codeLabel(TRANSPORT_TYPE, channel.transportType)}
          </span>
        </span>

        <dl className="grid gap-1 pl-8 text-sm">
          <div className="flex gap-2">
            <dt className="text-muted">{t('orders.newOrder.channel.transit')}</dt>
            <dd className="text-ink tabular-nums">
              {t('orders.newOrder.channel.transitDays', {
                min: fmt.number(channel.transitDaysMin),
                max: fmt.number(channel.transitDaysMax),
              })}
            </dd>
          </div>

          {/*
            Not a formula — MYP-53 slice 2 found `channels` carries no volumetric-divisor field at
            all, only which basis it charges by. `WEIGHT`/`VOL`/`BAG` is what a customer can
            actually act on: whether a light, bulky parcel is billed on its real weight or volume.
          */}
          <div className="flex gap-2">
            <dt className="text-muted">{t('orders.newOrder.channel.weightBasis')}</dt>
            <dd className="text-ink">{codeLabel(WEIGHT_BASIS, channel.weightBasis)}</dd>
          </div>
        </dl>

        {/*
          The vendor's own free-text notice (see `ShippingChannel.notice`). Undefined on 14 of 205
          channels, so it renders only when there is one.
        */}
        {channel.notice ? (
          <span className="block pl-8">
            <span className="sr-only">{t('orders.newOrder.channel.notice')}: </span>
            <span
              ref={noticeRef}
              id={noticeId}
              className={cn(
                'block text-sm whitespace-pre-line text-muted',
                !expanded && 'line-clamp-2',
              )}
            >
              {channel.notice}
            </span>
            {truncated || expanded ? (
              <button
                type="button"
                aria-expanded={expanded}
                aria-controls={noticeId}
                onClick={() => setExpanded((current) => !current)}
                className="relative z-10 inline-flex min-h-11 items-center text-sm font-medium text-brand hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand lg:mt-0.5 lg:min-h-0"
              >
                {expanded ? t('services.showLess') : t('services.showMore')}
              </button>
            ) : null}
          </span>
        ) : null}
      </div>
    </Card>
  );
}
